import React, { useState, useEffect } from "react";
import { normaliseHost } from '../tenant';
import {
  collection,
  onSnapshot,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
  setDoc,
  getDoc,
  getDocs,
  deleteField,
  serverTimestamp,
  query,
  orderBy,
  limit,
} from "firebase/firestore";
import { db, auth } from "../firebase";
import MigrationPanel from "./MigrationPanel";
import { signOut } from "firebase/auth";
import { useNavigate } from "react-router-dom";
import {
  LogOut,
  Plus,
  Server,
  Edit3,
  Trash2,
  CheckCircle,
  XCircle,
  Settings,
  Users,
  Database,
} from "lucide-react";
import Login from "./Login";
import { SystemSettingsContent } from "./SystemSettings";

const MODULE_LIST = [
  { id: "dashboard", name: "Dashboard" },
  { id: "my_message", name: "My Message" },
  { id: "talent_bank", name: "Talent Bank" },
  { id: "parent_notice", name: "Notice & Payment" },
  { id: "social_credit", name: "Student Credit" },
  { id: "borrow_devices", name: "Borrow IT Devices" },
  { id: "facility_booking", name: "Facility Booking" },
  { id: "inventory_user", name: "My Inventory" },
  { id: "attendance", name: "Attendance" },
  { id: "my_timetable", name: "My Timetable" },
  { id: "data_mgmt", name: "Data Management" },
  { id: "user_mgmt", name: "Account Management", locked: true },
  { id: "manage_it_assets", name: "Manage IT Assets" },
  { id: "campus_management", name: "Campus Management" },
  { id: "inventory_admin", name: "Inventory Management" },
  { id: "calendar_admin", name: "School Calendar" },
  { id: "manage_timetable", name: "Manage Timetable" },
  { id: "system_settings", name: "System Settings", locked: true },
];

const DeveloperDashboard = () => {
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authChecking, setAuthChecking] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingSchool, setEditingSchool] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [activeTab, setActiveTab] = useState("tenants");
  const [purging, setPurging] = useState(null);
  const [purgeLog, setPurgeLog] = useState("");
  // { schoolId: latest usage_snapshots document }. Fetched once per school rather than subscribed:
  // it changes monthly, so a live listener per tenant would be all cost and no benefit.
  const [snapshots, setSnapshots] = useState({});
  // { schoolId: metadata/health_report }. Written by the tenantHealthCheck task, read here so the
  // tenant list is where a problem surfaces -- a report nobody passes is a report nobody reads.
  const [health, setHealth] = useState({});
  const [snapshotBusy, setSnapshotBusy] = useState(false);
  const [developers, setDevelopers] = useState([]);
  const [newDeveloper, setNewDeveloper] = useState("");
  const [developersUnreadable, setDevelopersUnreadable] = useState(false);
  const [auditEntries, setAuditEntries] = useState([]);

  const [formData, setFormData] = useState({
    id: "",
    name: "",
    domain: "",
    licenseKey: "",
    licenseExpiresAt: "",
    subscription: "Basic",
    storageQuotaGB: 5,
    modules: MODULE_LIST.reduce((acc, mod) => ({ ...acc, [mod.id]: true }), {}),
  });

  const navigate = useNavigate();

  useEffect(() => {
    const checkAuth = auth.onAuthStateChanged(async (user) => {
      if (!user) {
        setIsAuthenticated(false);
        setAuthChecking(false);
        return;
      }

      try {
        const devSnap = await getDoc(doc(db, "developers", user.email));
        if (!devSnap.exists()) {
          navigate("/dashboard");
          return;
        }
        setIsAuthenticated(true);
        setAuthChecking(false);
      } catch (err) {
        console.error("Auth check error", err);
        setIsAuthenticated(false);
        setAuthChecking(false);
      }
    });

    return () => checkAuth();
  }, [navigate]);

  const loadSnapshots = async (list) => {
    const out = {};
    await Promise.all(list.map(async (sc) => {
      try {
        const q = query(
          collection(db, "schools", sc.id, "usage_snapshots"),
          orderBy("period", "desc"),
          limit(1),
        );
        const res = await getDocs(q);
        if (!res.empty) out[sc.id] = res.docs[0].data();
      } catch (e) {
        // No snapshots yet, or unreadable. The card just says so.
      }
    }));
    setSnapshots(out);

    const hOut = {};
    await Promise.all(list.map(async (sc) => {
      try {
        const h = await getDoc(doc(db, "schools", sc.id, "metadata", "health_report"));
        if (h.exists()) hOut[sc.id] = h.data();
      } catch (e) {
        // Never checked, or unreadable.
      }
    }));
    setHealth(hOut);
  };

  useEffect(() => {
    if (authChecking) return;

    const unsub = onSnapshot(
      collection(db, "schools"),
      async (snap) => {
        const list = [];
        const promises = [];
        snap.forEach((d) => {
          const schoolData = { id: d.id, ...d.data() };
          list.push(schoolData);
          promises.push(
            getDoc(
              doc(db, "schools", d.id, "metadata", "system_settings"),
            ).then((sSnap) => {
              if (sSnap.exists() && sSnap.data().entitledModules) {
                schoolData.modules = sSnap.data().entitledModules;
              }
            }),
          );
        });
        await Promise.all(promises);
        setSchools(list);
        setLoading(false);
        // Latest billing figures per tenant. Not awaited: the tenant list should not wait on numbers
        // that are only there for reference, and a school with no snapshot yet just says so.
        loadSnapshots(list);
      },
      (error) => {
        console.error(error);
        setLoading(false);
      },
    );
    return () => unsub();
  }, [authChecking]);

  // The platform-owner list and the record of what platform owners have done. Both live outside any
  // school, and both are only readable by a developer.
  useEffect(() => {
    if (authChecking || !isAuthenticated) return;

    const unsubDevs = onSnapshot(
      collection(db, "developers"),
      (snap) => setDevelopers(snap.docs.map((d) => d.id)),
      // A developer may read the whole collection (see firestore.rules). If this still fails, the
      // section below says the list could not be read rather than showing an empty one as though
      // nobody were listed.
      (e) => {
        console.error("Could not list developers", e);
        setDevelopersUnreadable(true);
      },
    );

    const unsubAudit = onSnapshot(
      query(collection(db, "_audit"), orderBy("at", "desc"), limit(50)),
      (snap) => setAuditEntries(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
      (e) => console.error("Could not read the audit log", e),
    );

    return () => { unsubDevs(); unsubAudit(); };
  }, [authChecking, isAuthenticated]);

  const handleAddDeveloper = async () => {
    const email = newDeveloper.trim().toLowerCase();
    if (!email.includes("@")) { alert("Enter a full email address."); return; }
    if (developers.includes(email)) { alert("Already a platform developer."); return; }
    if (!window.confirm(
      `Give ${email} platform developer access?\n\nThis is unrestricted access to EVERY school on `
      + `the platform — every student record, every salary figure, and the ability to delete a `
      + `tenant. It is the widest grant this system has.`)) return;

    try {
      await setDoc(doc(db, "developers", email), {
        addedBy: (auth.currentUser?.email || "").toLowerCase(),
        addedAt: serverTimestamp(),
      });
      logAudit("developer.add", { targetEmail: email });
      setNewDeveloper("");
    } catch (e) {
      alert("Could not add: " + e.message);
    }
  };

  const handleRemoveDeveloper = async (email) => {
    const own = (auth.currentUser?.email || "").trim().toLowerCase();
    if (email === own) {
      // Nothing else grants this access, so removing your own entry is a one-way door back to the
      // Firebase console.
      alert("You cannot remove your own developer access from here — you would lock yourself out.");
      return;
    }
    if (!window.confirm(`Remove platform developer access from ${email}?`)) return;
    try {
      await deleteDoc(doc(db, "developers", email));
      logAudit("developer.remove", { targetEmail: email });
    } catch (e) {
      alert("Could not remove: " + e.message);
    }
  };

  // Records a platform-owner action. Deliberately fire-and-forget: an audit write failing must not
  // stop the action, or the log becomes a reason things break. A missing entry is visible in the
  // sequence; a blocked suspension is not.
  const logAudit = (action, detail) => {
    const actorEmail = (auth.currentUser?.email || "").trim().toLowerCase();
    if (!actorEmail) return;
    addDoc(collection(db, "_audit"), {
      action,
      actorEmail,
      at: serverTimestamp(),
      ...detail,
    }).catch((e) => console.error("Could not write the audit entry", e));
  };

  const handleLogout = async () => {
    await signOut(auth);
    navigate("/");
  };

  const openModal = (school = null) => {
    if (school) {
      setEditingSchool(school);
      setFormData({
        id: school.id,
        name: school.name || "",
        domain: school.domain || "",
        licenseKey: school.licenseKey || "",
        licenseExpiresAt: school.licenseExpiresAt || "",
        subscription: school.subscription || "Basic",
        storageQuotaGB: school.storageQuotaBytes
          ? Math.round(school.storageQuotaBytes / (1024 * 1024 * 1024))
          : 5,
        modules: {
          ...MODULE_LIST.reduce(
            (acc, mod) => ({ ...acc, [mod.id]: false }),
            {},
          ),
          ...school.modules,
        },
      });
    } else {
      setEditingSchool(null);
      setFormData({
        id: "",
        name: "",
        domain: "",
        licenseKey: "",
        subscription: "Basic",
        storageQuotaGB: 5,
        modules: MODULE_LIST.reduce(
          (acc, mod) => ({ ...acc, [mod.id]: true }),
          {},
        ),
      });
    }
    setIsModalOpen(true);
  };

  const handleModuleToggle = (modId) => {
    const mod = MODULE_LIST.find((m) => m.id === modId);
    if (mod && mod.locked) return; // Prevent toggling locked modules

    setFormData((prev) => ({
      ...prev,
      modules: {
        ...prev.modules,
        [modId]: !prev.modules[modId],
      },
    }));
  };

  const generateLicenseKey = () => {
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let key = "";
    for (let i = 0; i < 16; i++) {
      key += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData((prev) => ({ ...prev, licenseKey: key }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSave = { ...formData };
      const docId = dataToSave.id;
      delete dataToSave.id; // Don't save the id inside the document fields

      const entitledModules = dataToSave.modules;
      delete dataToSave.modules; // Do not save on root document
      delete dataToSave.branding; // Just in case
      delete dataToSave.dashboardConfig;

      dataToSave.storageQuotaBytes =
        Number(dataToSave.storageQuotaGB) * 1024 * 1024 * 1024;
      delete dataToSave.storageQuotaGB;

      if (editingSchool) {
        await updateDoc(doc(db, "schools", editingSchool.id), dataToSave);
        logAudit("tenant.update", { schoolId: editingSchool.id, schoolName: dataToSave.name || "" });
        await setDoc(
          doc(db, "schools", editingSchool.id, "metadata", "system_settings"),
          { entitledModules },
          { merge: true },
        );
      } else {
        if (!docId) {
          alert("School ID is required.");
          return;
        }
        await setDoc(doc(db, "schools", docId), dataToSave);
        logAudit("tenant.create", { schoolId: docId, schoolName: dataToSave.name || "" });

        // Copy global system settings to the new school
        try {
          const globalSnap = await getDoc(
            doc(db, "metadata", "system_settings"),
          );
          if (globalSnap.exists()) {
            await setDoc(
              doc(db, "schools", docId, "metadata", "system_settings"),
              { ...globalSnap.data(), entitledModules },
              { merge: true },
            );
          } else {
            await setDoc(
              doc(db, "schools", docId, "metadata", "system_settings"),
              { entitledModules },
              { merge: true },
            );
          }
        } catch (err) {
          console.error("Failed to copy default settings to new school:", err);
        }
      }
      setIsModalOpen(false);
    } catch (err) {
      alert("Error saving school: " + err.message);
    }
  };

  // Deleting the school document used to be the whole of "delete tenant". Firestore does not
  // cascade, so every subcollection survived -- and because no rule checked that the school existed,
  // the tenant's former staff kept full read and write on all of it, salary records included.
  //
  // Two steps now. Suspending is instant and reversible: schoolActive() in the rules turns on one
  // field, so nobody can read or write the tenant from the moment it is set. Purging is the
  // irreversible walk, and it refuses to run on a tenant that is not suspended first.
  const handleSuspend = async (school) => {
    const on = school.disabled !== true;
    if (on && !window.confirm(
      `Suspend ${school.name}?

Everybody at this school loses access immediately, including its `
      + `administrators. Nothing is deleted, and you can reactivate it at any time.`)) return;

    try {
      await updateDoc(doc(db, "schools", school.id), { disabled: on });
      logAudit(on ? "tenant.suspend" : "tenant.reactivate", {
        schoolId: school.id,
        schoolName: school.name || school.id,
      });
    } catch (err) {
      alert("Could not change the tenant's status: " + err.message);
    }
  };

  const handlePurge = async (school) => {
    if (school.disabled !== true) {
      alert("Suspend this tenant first. Purging a live tenant would race with whoever is still "
        + "writing to it and leave fragments behind.");
      return;
    }
    // Typed confirmation rather than OK/Cancel: this walks every subcollection and cannot be undone,
    // and the school's own name is the one thing a misclick will not produce.
    const typed = window.prompt(
      `This permanently deletes every record belonging to ${school.name} -- students, staff, `
      + `assessments, salary records, everything. It cannot be undone.

`
      + `Type the school name exactly to confirm:`);
    if (typed === null) return;
    if (typed.trim() !== (school.name || "").trim()) {
      alert("That did not match. Nothing was deleted.");
      return;
    }

    setPurging(school.id);
    // Logged BEFORE the purge, not after: if it fails halfway the attempt is still the thing worth
    // knowing about, and the tenant's own record is about to stop existing.
    logAudit("tenant.purge", { schoolId: school.id, schoolName: school.name || school.id });
    try {
      await runMaintenance({ task: "purgeTenant", schoolId: school.id });
    } catch (err) {
      setPurgeLog(err.message);
    }
    setPurging(null);
  };

  // Queues a task and waits for the function to write its result back onto the same document.
  //
  // The timeout is a SILENCE timeout, re-armed on every write: these walk every school and can
  // legitimately run for minutes, while a function killed by the platform never writes a result at
  // all -- so silence, not elapsed time, is what distinguishes dead from busy.
  const runMaintenance = (payload) => new Promise((resolve, reject) => {
    setPurgeLog("Starting…");
    let timer = null;
    let unsub = null;
    const arm = () => {
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => {
        if (unsub) unsub();
        reject(new Error("No response for two minutes. Check the Cloud Function logs — this is "
          + "safe to re-run."));
      }, 120000);
    };

    (async () => {
      try {
        const ref = doc(collection(db, "_maintenance"));
        await setDoc(ref, { ...payload, status: "PENDING", createdAt: serverTimestamp() });
        arm();
        unsub = onSnapshot(ref, (snapDoc) => {
          const d = snapDoc.data();
          if (!d) return;
          arm();
          if (d.status === "PENDING") {
            if (d.progress) setPurgeLog(d.progress);
            return;
          }
          clearTimeout(timer);
          unsub();
          if (d.status === "SUCCESS") { setPurgeLog(d.message || "Done."); resolve(); }
          else reject(new Error(d.message || "The task reported an error."));
        }, (e) => { if (timer) clearTimeout(timer); reject(e); });
      } catch (e) {
        if (timer) clearTimeout(timer);
        reject(e);
      }
    })();
  });


  const handleSnapshot = async () => {
    setSnapshotBusy(true);
    logAudit("usage.snapshot", {});
    try {
      await runMaintenance({ task: "takeUsageSnapshot" });
      await loadSnapshots(schools);
    } catch (err) {
      setPurgeLog(err.message);
    }
    setSnapshotBusy(false);
  };

  if (authChecking)
    return (
      <div className="p-8 text-center text-muted animate-fade-in">
        Checking permissions...
      </div>
    );

  if (!isAuthenticated) return <Login isDeveloperLogin={true} />;

  if (loading)
    return (
      <div className="p-8 text-center text-muted animate-fade-in">
        Loading Developer Dashboard...
      </div>
    );

  return (
    <div
      className="animate-fade-in"
      style={{
        backgroundColor: "var(--color-bg-app)",
        minHeight: "100vh",
        padding: "2rem",
      }}
    >
      <div style={{ maxWidth: "1200px", margin: "0 auto" }}>
        <header
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "2rem",
          }}
        >
          <div>
            <h1
              style={{
                fontSize: "1.75rem",
                fontWeight: "700",
                color: "var(--color-text-main)",
                display: "flex",
                alignItems: "center",
                gap: "0.75rem",
              }}
            >
              <Server size={28} color="var(--color-primary)" />
              Developer Administration
            </h1>
            <p style={{ color: "var(--color-text-muted)" }}>
              Manage school tenants, licenses, and module access.
            </p>
          </div>
          <div style={{ display: "flex", gap: "1rem", alignItems: "center" }}>
            <button
              onClick={() => navigate("/dashboard")}
              className="btn-primary"
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              Go to School Portal
            </button>
            <button
              onClick={handleLogout}
              className="btn-outline"
              style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
            >
              <LogOut size={16} /> Sign Out
            </button>
          </div>
        </header>

        <div
          style={{
            display: "flex",
            gap: "1rem",
            marginBottom: "1.5rem",
            borderBottom: "1px solid var(--color-border)",
            paddingBottom: "0.5rem",
          }}
        >
          <button
            onClick={() => setActiveTab("tenants")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.5rem 1rem",
              background: "none",
              border: "none",
              borderBottom:
                activeTab === "tenants"
                  ? "2px solid var(--color-primary)"
                  : "2px solid transparent",
              color:
                activeTab === "tenants"
                  ? "var(--color-primary)"
                  : "var(--color-text-muted)",
              fontWeight: activeTab === "tenants" ? "600" : "400",
              cursor: "pointer",
            }}
          >
            <Users size={18} /> School Tenants
          </button>
          <button
            onClick={() => setActiveTab("platform")}
            style={{
              padding: "0.75rem 1.25rem",
              border: "none",
              borderBottom:
                activeTab === "platform"
                  ? "2px solid var(--color-primary)"
                  : "2px solid transparent",
              color:
                activeTab === "platform"
                  ? "var(--color-primary)"
                  : "var(--color-text-muted)",
              backgroundColor: "transparent",
              cursor: "pointer",
              fontWeight: activeTab === "platform" ? "600" : "400",
            }}
          >
            Platform
          </button>
          <button
            onClick={() => setActiveTab("migration")}
            style={{
              padding: "0.75rem 1.25rem",
              border: "none",
              borderBottom:
                activeTab === "migration"
                  ? "2px solid var(--color-primary)"
                  : "2px solid transparent",
              color:
                activeTab === "migration"
                  ? "var(--color-primary)"
                  : "var(--color-text-muted)",
              backgroundColor: "transparent",
              cursor: "pointer",
              fontWeight: activeTab === "migration" ? "600" : "400",
            }}
          >
            Migration
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: "0.5rem",
              padding: "0.5rem 1rem",
              background: "none",
              border: "none",
              borderBottom:
                activeTab === "settings"
                  ? "2px solid var(--color-primary)"
                  : "2px solid transparent",
              color:
                activeTab === "settings"
                  ? "var(--color-primary)"
                  : "var(--color-text-muted)",
              fontWeight: activeTab === "settings" ? "600" : "400",
              cursor: "pointer",
            }}
          >
            <Settings size={18} /> Global System Settings
          </button>
        </div>

        {activeTab === "platform" ? (
          <div style={{ height: "calc(100vh - 200px)", overflowY: "auto", display: "flex", flexDirection: "column", gap: "2rem" }}>

            <div>
              <h2 style={{ fontSize: "1.2rem", fontWeight: 700, margin: 0 }}>
                Platform developers ({developers.length})
              </h2>
              <p style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", margin: "0.4rem 0 1rem", maxWidth: "68ch", lineHeight: 1.7 }}>
                Unrestricted access to <strong>every school on the platform</strong> — every student
                record, every salary figure, and the ability to delete a tenant. This is the widest
                grant the system has, which is why every change here is written to the log below.
              </p>

              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem", maxWidth: "560px" }}>
                {developers.length === 0 && (
                  <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                    {developersUnreadable
                      ? "The list could not be read — check the Firebase console rather than concluding it is empty."
                      : "None listed."}
                  </span>
                )}
                {developers.map((email) => (
                  <div
                    key={email}
                    style={{
                      display: "flex", alignItems: "center", justifyContent: "space-between",
                      gap: "1rem", padding: "0.6rem 0.85rem",
                      backgroundColor: "var(--color-bg-card)",
                      border: "1px solid var(--color-border)",
                      borderRadius: "var(--radius-md)",
                      fontSize: "0.875rem",
                    }}
                  >
                    <span>
                      {email}
                      {email === (auth.currentUser?.email || "").toLowerCase() && (
                        <span style={{ color: "var(--color-text-muted)", fontSize: "0.78rem" }}> — you</span>
                      )}
                    </span>
                    <button
                      onClick={() => handleRemoveDeveloper(email)}
                      style={{
                        background: "none", border: "none", cursor: "pointer",
                        color: "var(--color-danger)", fontSize: "0.8rem",
                      }}
                    >
                      Remove
                    </button>
                  </div>
                ))}

                <div style={{ display: "flex", gap: "0.5rem", marginTop: "0.5rem" }}>
                  <input
                    type="email"
                    className="input-field"
                    placeholder="new.developer@example.com"
                    value={newDeveloper}
                    onChange={(e) => setNewDeveloper(e.target.value)}
                    style={{ margin: 0, flex: 1 }}
                  />
                  <button className="btn-outline" onClick={handleAddDeveloper} style={{ width: "auto", padding: "0 1.1rem" }}>
                    Add
                  </button>
                </div>
              </div>
            </div>

            <div>
              <h2 style={{ fontSize: "1.2rem", fontWeight: 700, margin: 0 }}>Activity log</h2>
              <p style={{ color: "var(--color-text-muted)", fontSize: "0.85rem", margin: "0.4rem 0 1rem", maxWidth: "68ch", lineHeight: 1.7 }}>
                Append-only, most recent first. Nobody can edit or delete an entry, including you —
                a log that can be rewritten answers &ldquo;what happened&rdquo; with whatever the last
                writer preferred.
              </p>

              {auditEntries.length === 0 ? (
                <span style={{ fontSize: "0.85rem", color: "var(--color-text-muted)" }}>
                  Nothing recorded yet.
                </span>
              ) : (
                <div style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", overflow: "hidden" }}>
                  {auditEntries.map((entry) => (
                    <div
                      key={entry.id}
                      style={{
                        display: "flex", flexWrap: "wrap", gap: "0.3rem 1rem",
                        padding: "0.55rem 0.85rem",
                        borderBottom: "1px solid var(--color-border)",
                        fontSize: "0.82rem",
                        backgroundColor: "var(--color-bg-card)",
                      }}
                    >
                      <span style={{ fontFamily: "monospace", color: "var(--color-primary)", minWidth: "10rem" }}>
                        {entry.action}
                      </span>
                      <span style={{ color: "var(--color-text-muted)" }}>{entry.actorEmail}</span>
                      {entry.schoolName && <span>{entry.schoolName}</span>}
                      {entry.targetEmail && <span>{entry.targetEmail}</span>}
                      <span style={{ color: "var(--color-text-muted)", marginLeft: "auto" }}>
                        {entry.at?.toDate ? entry.at.toDate().toLocaleString() : "…"}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

          </div>
        ) : activeTab === "migration" ? (
          <div style={{ height: "calc(100vh - 200px)", overflowY: "auto" }}>
            <MigrationPanel />
          </div>
        ) : activeTab === "settings" ? (
          <div style={{ height: "calc(100vh - 200px)" }}>
            <SystemSettingsContent isDeveloperMode={true} />
          </div>
        ) : (
          <>
            {(purging || purgeLog) && (
              <div
                style={{
                  backgroundColor: "var(--color-bg-card)",
                  border: "1px solid var(--color-border)",
                  borderRadius: "var(--radius-md)",
                  padding: "0.9rem 1.1rem",
                  marginBottom: "1.25rem",
                  fontSize: "0.85rem",
                  lineHeight: 1.7,
                  whiteSpace: "pre-wrap",
                  color: "var(--color-text-muted)",
                }}
              >
                <strong style={{ color: "var(--color-text-main)" }}>
                  {purging ? "Purging…" : "Purge finished"}
                </strong>
                <br />
                {purgeLog}
                {!purging && (
                  <>
                    <br />
                    <button
                      onClick={() => setPurgeLog("")}
                      style={{
                        background: "none",
                        border: "none",
                        padding: 0,
                        marginTop: "0.5rem",
                        color: "var(--color-primary)",
                        cursor: "pointer",
                        fontSize: "0.8rem",
                      }}
                    >
                      Dismiss
                    </button>
                  </>
                )}
              </div>
            )}

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.75rem",
                marginBottom: "1.5rem",
              }}
            >
              <button
                onClick={handleSnapshot}
                disabled={snapshotBusy}
                title="Record this month's account and storage figures for every school"
                className="btn-outline"
                style={{ width: "auto", padding: "0 1.1rem" }}
              >
                {snapshotBusy ? "Recording…" : "Record usage snapshot"}
              </button>
              <button
                onClick={() => openModal()}
                className="btn-primary"
                style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}
              >
                <Plus size={18} /> Add New School Tenant
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gap: "1rem",
                gridTemplateColumns: "repeat(auto-fill, minmax(min(560px, 100%), 1fr))",
              }}
            >
              {schools.map((school) => (
                <div
                  key={school.id}
                  style={{
                    backgroundColor: "var(--color-bg-card)",
                    borderRadius: "var(--radius-lg)",
                    padding: "1.5rem",
                    boxShadow: "var(--shadow-sm)",
                    border: "1px solid var(--color-border)",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "flex-start",
                      marginBottom: "1rem",
                    }}
                  >
                    <div>
                      <h2
                        style={{
                          fontSize: "1.25rem",
                          fontWeight: "600",
                          margin: "0 0 0.25rem 0",
                        }}
                      >
                        {school.name}
                        {school.disabled && (
                          <span
                            style={{
                              marginLeft: "0.6rem",
                              fontSize: "0.68rem",
                              fontWeight: 700,
                              letterSpacing: "0.06em",
                              textTransform: "uppercase",
                              padding: "0.15rem 0.45rem",
                              borderRadius: "3px",
                              backgroundColor: "var(--color-danger-bg)",
                              color: "var(--color-danger)",
                              verticalAlign: "middle",
                            }}
                          >
                            Suspended
                          </span>
                        )}
                      </h2>
                      <div
                        style={{
                          fontSize: "0.875rem",
                          color: "var(--color-primary)",
                        }}
                      >
                        {school.domain}
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: "0.5rem" }}>
                      <button
                        onClick={() => openModal(school)}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: "var(--color-text-muted)",
                        }}
                      >
                        <Edit3 size={18} />
                      </button>
                      <button
                        onClick={() => handleSuspend(school)}
                        title={school.disabled ? "Reactivate this tenant" : "Suspend this tenant"}
                        style={{
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          fontSize: "0.78rem",
                          color: school.disabled
                            ? "var(--color-primary)"
                            : "var(--color-text-muted)",
                        }}
                      >
                        {school.disabled ? "Reactivate" : "Suspend"}
                      </button>
                      <button
                        onClick={() => handlePurge(school)}
                        disabled={!school.disabled || purging === school.id}
                        title={
                          school.disabled
                            ? "Permanently delete every record"
                            : "Suspend the tenant before purging it"
                        }
                        style={{
                          background: "none",
                          border: "none",
                          cursor: school.disabled ? "pointer" : "not-allowed",
                          opacity: school.disabled ? 1 : 0.35,
                          color: "var(--color-danger)",
                        }}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      display: "flex",
                      gap: "1rem",
                      marginBottom: "1rem",
                    }}
                  >
                    <span
                      style={{
                        fontSize: "0.75rem",
                        padding: "0.2rem 0.5rem",
                        backgroundColor: "var(--color-warning-bg)",
                        color: "var(--color-warning-text)",
                        borderRadius: "4px",
                        fontWeight: "600",
                      }}
                    >
                      Plan: {school.subscription || "Basic"}
                    </span>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        padding: "0.2rem 0.5rem",
                        backgroundColor: "var(--color-info-bg)",
                        color: "var(--color-info-text)",
                        borderRadius: "4px",
                        fontWeight: "600",
                      }}
                    >
                      Key: {school.licenseKey || "N/A"}
                      {school.licenseExpiresAt && (() => {
                        const left = Math.ceil(
                          (new Date(school.licenseExpiresAt) - new Date()) / 86400000,
                        );
                        if (left < 0) {
                          return (
                            <span style={{ color: "var(--color-danger)", fontWeight: 700 }}>
                              {" · licence expired " + (-left) + " day(s) ago"}
                            </span>
                          );
                        }
                        if (left <= 30) {
                          return (
                            <span style={{ color: "var(--color-danger)" }}>
                              {" · licence expires in " + left + " day(s)"}
                            </span>
                          );
                        }
                        return (
                          <span style={{ color: "var(--color-text-muted)" }}>
                            {" · until " + school.licenseExpiresAt}
                          </span>
                        );
                      })()}
                    </span>
                    <span
                      style={{
                        fontSize: "0.75rem",
                        padding: "0.2rem 0.5rem",
                        backgroundColor: "var(--color-success-bg-soft)",
                        color: "var(--color-success-text)",
                        borderRadius: "4px",
                        fontWeight: "600",
                      }}
                    >
                      Storage: {(school.storageUsedBytes ? school.storageUsedBytes / (1024 * 1024 * 1024) : 0).toFixed(2)} GB / {school.storageQuotaBytes ? Math.round(school.storageQuotaBytes / (1024 * 1024 * 1024)) : 5} GB
                    </span>
                  </div>

                  {(() => {
                    const h = health[school.id];
                    if (!h) return null;
                    if (h.healthy && !(h.notes || []).length) {
                      return (
                        <div
                          style={{
                            marginTop: "0.9rem",
                            fontSize: "0.8rem",
                            color: "var(--color-text-muted)",
                          }}
                        >
                          Health check: nothing found
                        </div>
                      );
                    }
                    const problems = h.problems || [];
                    const notes = h.notes || [];
                    return (
                      <div
                        style={{
                          marginTop: "0.9rem",
                          padding: "0.7rem 0.85rem",
                          borderRadius: "var(--radius-md)",
                          border: "1px solid",
                          borderColor: problems.length
                            ? "var(--color-danger)"
                            : "var(--color-border)",
                          backgroundColor: problems.length
                            ? "var(--color-danger-bg)"
                            : "var(--color-bg-app)",
                          fontSize: "0.82rem",
                          lineHeight: 1.75,
                        }}
                      >
                        {problems.map((t, i) => (
                          <div key={i} style={{ color: "var(--color-danger)" }}>
                            <strong>⚠ {t}</strong>
                          </div>
                        ))}
                        {notes.map((t, i) => (
                          <div key={i} style={{ color: "var(--color-text-muted)" }}>
                            {t}
                          </div>
                        ))}
                      </div>
                    );
                  })()}

                  {/* Usage, on its own row. It belongs beside the plan and storage chips in meaning
                      but not in form: chips are single values, this is a small table of them. */}
                  <div
                    style={{
                      marginTop: "0.9rem",
                      padding: "0.7rem 0.85rem",
                      borderRadius: "var(--radius-md)",
                      backgroundColor: "var(--color-bg-app)",
                      border: "1px solid var(--color-border)",
                      fontSize: "0.82rem",
                      lineHeight: 1.7,
                    }}
                  >
                    {(() => {
                      const us = snapshots[school.id];
                      if (!us) {
                        return (
                          <span style={{ color: "var(--color-text-muted)" }}>
                            No usage snapshot yet — use “Record usage snapshot” above to start the record
                          </span>
                        );
                      }
                      const by = us.accountsByRole || {};
                      const staff =
                        (by.system_admin || 0) + (by.teacher || 0) + (by.non_teaching || 0);
                      return (
                        <div
                          style={{
                            display: "grid",
                            gridTemplateColumns:
                              "repeat(auto-fit, minmax(110px, 1fr))",
                            gap: "0.5rem 1rem",
                          }}
                        >
                          <div>
                            <div style={{ color: "var(--color-text-muted)", fontSize: "0.72rem" }}>
                              Billable accounts · {us.period}
                            </div>
                            <strong style={{ fontSize: "1.05rem" }}>
                              {us.accountsBillable ?? "—"}
                            </strong>
                          </div>
                          <div>
                            <div style={{ color: "var(--color-text-muted)", fontSize: "0.72rem" }}>
                              Staff
                            </div>
                            <strong>{staff}</strong>
                          </div>
                          <div>
                            <div style={{ color: "var(--color-text-muted)", fontSize: "0.72rem" }}>
                              Student accounts
                            </div>
                            <strong>{by.student ?? "—"}</strong>
                          </div>
                          <div>
                            <div style={{ color: "var(--color-text-muted)", fontSize: "0.72rem" }}>
                              Parent accounts
                            </div>
                            <strong>{by.parent ?? "—"}</strong>
                          </div>
                          <div>
                            <div style={{ color: "var(--color-text-muted)", fontSize: "0.72rem" }}>
                              Students enrolled
                            </div>
                            <strong>{us.studentsEnrolled ?? "—"}</strong>
                          </div>
                          {us.accountsUnrecognised > 0 && (
                            <div>
                              <div style={{ color: "var(--color-danger)", fontSize: "0.72rem" }}>
                                Unrecognised role
                              </div>
                              <strong style={{ color: "var(--color-danger)" }}>
                                {us.accountsUnrecognised}
                              </strong>
                            </div>
                          )}
                        </div>
                      );
                    })()}
                  </div>

                  <div
                    style={{
                      marginTop: "auto",
                      paddingTop: "1rem",
                      borderTop: "1px solid var(--color-border)",
                    }}
                  >
                    <h3
                      style={{
                        fontSize: "0.875rem",
                        fontWeight: "600",
                        marginBottom: "0.5rem",
                        color: "var(--color-text-muted)",
                      }}
                    >
                      Enabled Modules
                    </h3>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "0.5rem",
                      }}
                    >
                      {MODULE_LIST.map((mod) =>
                        school.modules && school.modules[mod.id] ? (
                          <span
                            key={mod.id}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0.2rem",
                              fontSize: "0.7rem",
                              padding: "0.15rem 0.4rem",
                              backgroundColor: "var(--color-success-bg)",
                              color: "var(--color-success-text)",
                              borderRadius: "4px",
                            }}
                          >
                            <CheckCircle size={10} /> {mod.name}
                          </span>
                        ) : null,
                      )}
                    </div>
                  </div>
                </div>
              ))}
              {schools.length === 0 && (
                <div
                  style={{
                    gridColumn: "1 / -1",
                    padding: "3rem",
                    textAlign: "center",
                    color: "var(--color-text-muted)",
                    border: "2px dashed var(--color-border)",
                    borderRadius: "var(--radius-lg)",
                  }}
                >
                  No school tenants found. Click "Add New School Tenant" to
                  create one.
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {isModalOpen && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "var(--color-overlay)",
            zIndex: 999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              backgroundColor: "var(--color-bg-card)",
              padding: "2rem",
              borderRadius: "var(--radius-lg)",
              width: "100%",
              maxWidth: "600px",
              maxHeight: "90vh",
              overflowY: "auto",
            }}
          >
            <h2
              style={{
                fontSize: "1.25rem",
                fontWeight: "600",
                marginBottom: "1.5rem",
              }}
            >
              {editingSchool ? "Edit School Tenant" : "New School Tenant"}
            </h2>

            <form
              onSubmit={handleSubmit}
              style={{
                display: "flex",
                flexDirection: "column",
                gap: "1.25rem",
              }}
            >
              <div>
                <label className="input-label">School Name</label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({ ...formData, name: e.target.value })
                  }
                  required
                  placeholder="e.g. Springfield High"
                />
              </div>

              {!editingSchool && (
                <div>
                  <label className="input-label">
                    School ID (Firestore Doc ID)
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.id}
                    onChange={(e) =>
                      setFormData({ ...formData, id: e.target.value })
                    }
                    required
                    placeholder="e.g. hsss"
                  />
                  <p
                    style={{
                      fontSize: "0.75rem",
                      color: "var(--color-text-muted)",
                      marginTop: "0.25rem",
                    }}
                  >
                    This ID connects the tenant to their database collections.
                    It cannot be changed later.
                  </p>
                </div>
              )}

              <div>
                <label className="input-label">
                  Subdomain / URL Identifier
                </label>
                <input
                  type="text"
                  className="input-field"
                  value={formData.domain}
                  onChange={(e) =>
                    // Tidied as it is typed: a pasted protocol or path is not part of a host, and
                    // stray case would store an address no visitor could match.
                    setFormData({ ...formData, domain: normaliseHost(e.target.value) })
                  }
                  required
                  placeholder="e.g. springfield.d-admin.com"
                />
                <p style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", margin: "0.3rem 0 0", lineHeight: 1.6 }}>
                  The school&rsquo;s address, in full. The first label is what identifies it, so a
                  school reached at a second address &mdash; its own domain, or the Firebase one
                  &mdash; needs the same first label there.
                </p>
              </div>

              <div style={{ display: "flex", gap: "1rem" }}>
                <div style={{ flex: 1 }}>
                  <label className="input-label">Subscription Tier</label>
                  <select
                    className="input-field"
                    value={formData.subscription}
                    onChange={(e) =>
                      setFormData({ ...formData, subscription: e.target.value })
                    }
                  >
                    <option value="Basic">Basic</option>
                    <option value="Pro">Pro</option>
                    <option value="Enterprise">Enterprise</option>
                  </select>
                </div>
                <div style={{ marginBottom: "1rem" }}>
                  <label className="input-label">Licence expires</label>
                  <input
                    type="date"
                    className="input-field"
                    value={formData.licenseExpiresAt || ""}
                    onChange={(e) =>
                      setFormData({ ...formData, licenseExpiresAt: e.target.value })
                    }
                  />
                  <p style={{ fontSize: "0.75rem", color: "var(--color-text-muted)", margin: "0.3rem 0 0", lineHeight: 1.6 }}>
                    Leave blank for no expiry. An expired licence is <strong>flagged, not
                    enforced</strong> &mdash; suspending a school is a commercial decision, and a
                    mistyped date should not lock out a thousand people overnight.
                  </p>
                </div>
                <div style={{ flex: 1 }}>
                  <label className="input-label">Storage Quota (GB)</label>
                  <input
                    type="number"
                    min="1"
                    max="1000"
                    className="input-field"
                    value={formData.storageQuotaGB}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        storageQuotaGB: e.target.value,
                      })
                    }
                  />
                </div>
              </div>

              <div style={{ display: "flex", gap: "1rem" }}>
                <div style={{ flex: 1 }}>
                  <label
                    className="input-label"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <span>License Key</span>
                    <button
                      type="button"
                      onClick={generateLicenseKey}
                      style={{
                        background: "none",
                        border: "none",
                        color: "var(--color-primary)",
                        fontSize: "0.75rem",
                        cursor: "pointer",
                        fontWeight: "600",
                        padding: 0,
                      }}
                    >
                      Generate
                    </button>
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={formData.licenseKey}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        licenseKey: e.target.value.toUpperCase(),
                      })
                    }
                    placeholder="XXXXXXXXXXXXXXXX"
                  />
                </div>
              </div>

              <div style={{ marginTop: "1rem" }}>
                <label
                  className="input-label"
                  style={{ marginBottom: "0.75rem" }}
                >
                  Module Permissions
                </label>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: "1fr 1fr",
                    gap: "0.75rem",
                  }}
                >
                  {MODULE_LIST.map((mod) => (
                    <label
                      key={mod.id}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        cursor: mod.locked ? "not-allowed" : "pointer",
                        fontSize: "0.875rem",
                        opacity: mod.locked ? 0.6 : 1,
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={mod.locked ? true : !!formData.modules[mod.id]}
                        onChange={() => handleModuleToggle(mod.id)}
                        disabled={mod.locked}
                        style={{
                          width: "16px",
                          height: "16px",
                          cursor: mod.locked ? "not-allowed" : "pointer",
                        }}
                      />
                      {mod.name}{" "}
                      {mod.locked && (
                        <span
                          style={{
                            fontSize: "0.7rem",
                            color: "var(--color-text-muted)",
                          }}
                        >
                          (Required)
                        </span>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: "1rem",
                  marginTop: "1rem",
                  paddingTop: "1.5rem",
                  borderTop: "1px solid var(--color-border)",
                }}
              >
                <button
                  type="button"
                  className="btn-outline"
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Save Tenant
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default DeveloperDashboard;
